import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTableEndlessPaginated } from '@/components/data-table/data-table-endless-paginated';
import {
  EmptyStateProps,
  StickyTableStateContent,
} from '@/components/data-table/data-table-states';

// TODO: add extends
interface GenerateEntityTableProps<T> {
  table: Table<T>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  isAnyFilterApplied?: boolean;
  totalCount?: number;
  onRowClick?: (entity: T) => void;
  shouldAnimateRowEnter?: (entity: T) => boolean;
  stickyHeader?: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
}

export const GenerateEntityTable = <T,>({
  isAnyFilterApplied,
  totalCount,
  onRowClick,
  stickyHeader = false,
  tableId,
  onResizeEnd,
  ...paginatedTableProps
}: GenerateEntityTableProps<T>) => {
  const { t } = useTranslation(['authenticated.projects', 'common']);

  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    // If there's any filter but no results, show "No results found"
    if (isAnyFilterApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    // If there are no projects at all (total count is 0), show empty state with buttons
    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start px-8 text-left">
              <div className="flex flex-col">
                <div className="text-sm font-medium">
                  {t('authenticated.projects:table.noProjectsYet')}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('authenticated.projects:table.noProjectsDescription')}
                </div>
              </div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginated
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      onRowClick={onRowClick}
      stickyHeader={stickyHeader}
      variant={'generateInvoices'}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
    />
  );
};
